/**
 * The "Public listing" step: opt in to the MCOS provider directory.
 *
 * Brian, 30 September 2026: "Add a 'Public listing' step (at sign-up, and
 * editable later in the clinic's settings)". Off by default. Shown on the
 * application page a provider lands on after creating their account, and in
 * Settings for anyone who wants to change it later. The practice address that
 * the website's map uses is the one already collected on the application, so
 * this form asks only for what is new.
 *
 * What the website receives is only what is on this form plus the practice
 * name and address. Never an NPI, licence number, DEA, personal email or
 * phone, or anything about patients.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, Field, InfoHint, Toggle, inputClass } from './ui';

interface Listing {
  listed: boolean;
  displayName: string | null;
  specialtyLine: string | null;
  publicPhone: string | null;
  bookingUrl: string | null;
  websiteUrl: string | null;
  inPerson: boolean;
  telehealth: boolean;
  telehealthStates: string[];
  acceptingNewPatients: boolean;
  services: string[];
  photoUrl: string | null;
  defaults: { displayName: string; specialtyLine: string | null; telehealthStates: string[] };
  appears: boolean;
  blockedBy: string | null;
  services_available: string[];
}

export function PublicListingCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['provider-listing'],
    queryFn: () => api.get<{ listing: Listing }>('/providers/me/listing'),
    retry: false,
  });

  const [draft, setDraft] = useState<Listing | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setDraft(query.data.listing);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (listing: Listing) =>
      api.put<{ listing: Listing }>('/providers/me/listing', {
        listed: listing.listed,
        displayName: listing.displayName,
        specialtyLine: listing.specialtyLine,
        publicPhone: listing.publicPhone,
        bookingUrl: listing.bookingUrl,
        websiteUrl: listing.websiteUrl,
        inPerson: listing.inPerson,
        telehealth: listing.telehealth,
        telehealthStates: listing.telehealth ? listing.telehealthStates : [],
        acceptingNewPatients: listing.acceptingNewPatients,
        services: listing.services,
        photoUrl: listing.photoUrl,
      }),
    onSuccess: (result) => {
      setError(null);
      setMessage(result.listing.listed ? 'Saved. Your listing is on.' : 'Saved. Your listing is off.');
      queryClient.setQueryData(['provider-listing'], result);
    },
    onError: (caught) => {
      setMessage(null);
      setError(caught instanceof Error ? caught.message : 'Could not save your listing');
    },
  });

  // A staff account has no provider profile, so there is nothing to list.
  if (query.isError || !draft) return null;

  const set = (patch: Partial<Listing>) => setDraft({ ...draft, ...patch });
  const clean = (value: string) => (value.trim() === '' ? null : value);
  const toggleService = (service: string) =>
    set({
      services: draft.services.includes(service)
        ? draft.services.filter((s) => s !== service)
        : [...draft.services, service],
    });

  return (
    <Card
      title="Public listing"
      subtitle="Appear on the MCOS provider directory, where patients find nearby clinics."
      action={
        <InfoHint label="About the public listing">
          Off by default. Only approved, active providers who turn this on are shown on the
          website map. Only the details on this page and your practice name and address are
          shared, never your NPI, licence numbers, personal contact details or anything about
          patients.
        </InfoHint>
      }
    >
      <div className="space-y-4">
        <Toggle
          checked={draft.listed}
          onChange={(next) => set({ listed: next })}
          label="List my practice in the MCOS provider directory"
          description="Turn it off at any time and you are removed from the map."
        />

        {draft.listed && draft.blockedBy && (
          <p className="rounded-sm2 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="listing-blocked">
            Not shown yet: {draft.blockedBy}.
          </p>
        )}

        <div className={draft.listed ? 'space-y-4' : 'space-y-4 opacity-60'} aria-disabled={!draft.listed}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Public display name" hint={`Blank uses ${draft.defaults.displayName}`}>
              <input className={inputClass} value={draft.displayName ?? ''} maxLength={120}
                onChange={(e) => set({ displayName: clean(e.target.value) })} />
            </Field>
            <Field label="Specialty line" hint={draft.defaults.specialtyLine ? `Blank uses ${draft.defaults.specialtyLine}` : 'Optional'}>
              <input className={inputClass} value={draft.specialtyLine ?? ''} maxLength={160}
                onChange={(e) => set({ specialtyLine: clean(e.target.value) })} />
            </Field>
            <Field label="Public phone" hint="May differ from your personal phone. Blank uses the practice phone.">
              <input className={inputClass} type="tel" value={draft.publicPhone ?? ''} maxLength={30}
                onChange={(e) => set({ publicPhone: clean(e.target.value) })} />
            </Field>
            <Field label="Booking link" hint="Optional. Must start with https://">
              <input className={inputClass} type="url" placeholder="https://" value={draft.bookingUrl ?? ''}
                onChange={(e) => set({ bookingUrl: clean(e.target.value) })} />
            </Field>
            <Field label="Website" hint="Optional. Must start with https://">
              <input className={inputClass} type="url" placeholder="https://" value={draft.websiteUrl ?? ''}
                onChange={(e) => set({ websiteUrl: clean(e.target.value) })} />
            </Field>
            <Field label="Headshot link" hint="Optional. Must start with https://">
              <input className={inputClass} type="url" placeholder="https://" value={draft.photoUrl ?? ''}
                onChange={(e) => set({ photoUrl: clean(e.target.value) })} />
            </Field>
          </div>

          <fieldset>
            <legend className="text-xs font-medium text-ink">Services</legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {draft.services_available.map((service) => {
                const active = draft.services.includes(service);
                return (
                  <button key={service} type="button" aria-pressed={active} onClick={() => toggleService(service)}
                    className={`rounded-full border px-2.5 py-1 text-xs transition ${
                      active ? 'border-slate-900 bg-slate-900 text-white' : 'border-ink/10 bg-white text-ink hover:border-slate-400'
                    }`}>
                    {service}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="divide-y divide-slate-100">
            <Toggle checked={draft.inPerson} onChange={(next) => set({ inPerson: next })} label="Sees patients in person" />
            <Toggle checked={draft.telehealth} onChange={(next) => set({ telehealth: next })} label="Offers telehealth" />
            <Toggle checked={draft.acceptingNewPatients} onChange={(next) => set({ acceptingNewPatients: next })}
              label="Accepting new patients" description="Change this any time; the map updates." />
          </div>

          {draft.telehealth && (
            <Field label="Telehealth states" hint={`Two-letter codes, separated by commas. Blank uses your licence states${draft.defaults.telehealthStates.length ? `: ${draft.defaults.telehealthStates.join(', ')}` : ''}.`}>
              <input className={inputClass} value={draft.telehealthStates.join(', ')}
                onChange={(e) => set({ telehealthStates: e.target.value.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter((s) => s.length === 2) })} />
            </Field>
          )}
        </div>

        {error && <p role="alert" className="rounded-sm2 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</p>}
        {message && <p className="text-xs text-ink-muted" data-testid="listing-message">{message}</p>}

        <Button loading={save.isPending} onClick={() => save.mutate(draft)}>
          Save listing
        </Button>
      </div>
    </Card>
  );
}
